import { baseCrudFactory } from "~/stores/base.crud.store.ts"
import { Api } from "~/api/Api.ts"
import {
    useProjetStore,
    useDataCacheStore,
    useAuthStore,
    useAdresseStore,
    useInterventionStore,
} from "~/stores"
import { RdvModel } from "~/model/rdv.model"
import { CALENDAR_DATE_VIEW, groupById, mapById } from "~/helpers"
import { DevisResource } from "~/api"

export const useRdvStore = baseCrudFactory({
    endpoint: "rdvs",
    primaryKey: "IDRDV",
    title: "rdv",
    startDateKey: "RDV_DEBUT",
    endDateKey: "RDV_FIN",
    model: RdvModel,
    modules: {
        projet: () => useProjetStore(),
        adresse: () => useAdresseStore(),
    },
    state: {
        viewSelect: CALENDAR_DATE_VIEW[0].value,
        idPersonnelImpersonate: null,
        hiddenDays: [0],
        displayAdresseAfterTitle: false,
        selectRdvInterventionType: [0],
        // Legende en surimpression du calendrier (natures de RDV, interventions), repliable
        legendeOuverte: true,
        formMode: null as null | "create" | "reschedule",
    },
    actions: {
        initializeAgendaType() {
            if (!this.shouldShowTypeSelector) {
                this.selectRdvInterventionType = this.defaultRdvInterventionType
            }
        },
        prepareCreate(prefill: Record<string, any> = {}) {
            this.currentItemId = null
            this.formItem = { ...prefill }
            this.formMode = "create"
            this.formItemCache = true
        },
        startReschedule(id) {
            const editItem = this.items.find((item) => item.IDRDV === id)
            if (!editItem) return
            this.currentItemId = id
            this.formItem = { ...editItem }
            this.formMode = "reschedule"
            this.formItemCache = true
            this.dialogView = false
        },
        rescheduleEvent(id) {
            this.startReschedule(id)
        },
        suspendForSubflow() {
            this.formItemCache = true
        },
        closeForm() {
            this.formItem = {}
            this.formItemCache = false
            this.formMode = null
        },
        async rescheduleToDate(id, rdvDebut, rdvFin) {
            const response = await Api.rdv.rdvReschedule(String(id), {
                RDV_DEBUT: rdvDebut,
                RDV_FIN: rdvFin,
            })
            this.setItems(response, false)
            return response
        },
        cancelCache() {
            this.currentItemId = null
            this.formItem = null
            this.formMode = null
        },
        async fetchTeamRdv(params) {
            const response =
                await Api.equipe.equipeLocalisationPersonnelRendezVous(params)
            this.setItems(response)
            return response
        },
        async debriefRdv(idrdv, params) {
            const rdv = await Api.rdv.rdvDebriefRdv(idrdv, params)
            this.setItems(rdv, false)
        },
        async compteRenduMetre(idRdv, payload) {
            const intervention = await Api.rdv.rdvCompteRenduMetre(
                String(idRdv),
                payload,
            )
            useInterventionStore().setItems(intervention, false)
            return intervention
        },
        isNatureCommercialOrMetreById(id) {
            const datacacheStore = useDataCacheStore()
            const nature = datacacheStore.natureRdvById[id]
            return nature?.CODE === "Com" || nature?.CODE === "Metre"
        },
        isNatureWithCodeById(id) {
            const datacacheStore = useDataCacheStore()
            const nature = datacacheStore.natureRdvById[id]
            return !!nature?.CODE
        },
    },
    getters: {
        dataCacheStore() {
            return useDataCacheStore()
        },
        authStore() {
            return useAuthStore()
        },
        canViewRdv() {
            return (
                this.authStore.isCommercial ||
                this.authStore.isCommercialDirector ||
                this.authStore.isMetreur
            )
        },
        canViewInterventions() {
            return this.authStore.isTechnician
        },
        shouldShowTypeSelector() {
            return this.canViewRdv && this.canViewInterventions
        },
        defaultRdvInterventionType() {
            if (this.canViewRdv && !this.canViewInterventions) {
                return [0] // RDV uniquement
            }
            if (!this.canViewRdv && this.canViewInterventions) {
                return [1] // Interventions uniquement
            }
            return [0] // Par défaut RDV si les deux rôles
        },
        activeEvents() {
            if (!this.itemsByDate?.length) return []

            const userId =
                this.idPersonnelImpersonate ||
                this.authStore.mainToken.tokenable_id

            return this.itemsByDate
                .filter(
                    (event) =>
                        !event.SUPPRIME &&
                        event.IDPERSONNELS?.includes(userId) &&
                        !event.RDV_PERSONNEL?.some(
                            (p) => p.IDPERSONNEL === userId && p.SUPPRIME == 1,
                        ),
                )
                .map((event) => event.getFormatted())
        },
        rdvByIdProjet() {
            return groupById(this.items, "IDPROJET") || {}
        },
        rdvAfterNowDate() {
            return this.items.filter(
                (rdv) => new Date(rdv.RDV_DEBUT) >= new Date(),
            )
        },
        isMoveRdv() {
            return this.formMode === "reschedule"
        },
        isExternalCreate() {
            return this.formMode === "create"
        },
    },
})
